/* Copyright (c) 2020-2023 Nordcloud Oy or its affiliates. All Rights Reserved. */

import { useState } from "react";
import { Row, Col, Hidden } from "react-awesome-styled-grid";
import { When } from "react-if";
import { Box, Text, theme, ModalConfirm, FlexContainer } from "@nordcloud/gnui";
import { useRemoveEnvFromOrgUnitV2Mutation } from "~/generated/graphql";
import { ExpandIcon, GlobalSpinner, WasteIndicator } from "~/components";
import {
  ApplicationItemDescription,
  EnvironmentDetails,
} from "~/components/ApplicationItem";
import { ERROR_TEXT, SUCCESS_TEXT } from "~/constants";
import { useToggle } from "~/hooks";
import { ROUTES } from "~/routing/routes";
import { showError, showSuccess } from "~/services/toast";
import { generateActionConfirmText } from "~/tools";
import { ApplicationItemCell } from "./ApplicationItemCell";
import { MappedApp } from "./utils";

type Props = {
  application: MappedApp;
  orgUnitId: string;
  onRemoveEnvFromOrgUnit: () => void;
};

export function ApplicationItem({
  application,
  orgUnitId,
  onRemoveEnvFromOrgUnit,
}: Props) {
  const [isExpanded, toggleIsExpanded] = useToggle();
  const [isModalConfirmRemoveOpen, setIsModalConfirmRemoveOpen] =
    useState(false);
  const [environmentToRemove, setEnvironmentToRemove] = useState({
    nid: "",
    name: "",
  });

  const [removeEnvFromOrgUnit, { loading: isLoadingRemoveEnvFromOrgUnit }] =
    useRemoveEnvFromOrgUnitV2Mutation();

  const appMonthlySavingSuggestions = application?.envs
    ?.map((env) => Number(env.cloudWasteAggregate?.cost))
    .filter((item) => !isNaN(item))
    .reduce((a, b) => a + b, 0);

  const onRemoveEnvFromOrgUnitHandler = async (envId: string) => {
    try {
      setIsModalConfirmRemoveOpen(false);

      await removeEnvFromOrgUnit({ variables: { envId, orgUnitId } });

      showSuccess(SUCCESS_TEXT.environmentRemovedFromOrgUnit);
      onRemoveEnvFromOrgUnit();
    } catch {
      showError(ERROR_TEXT.default);
    }
  };

  const handleRemoveEnvironment = (item: typeof environmentToRemove) => {
    setEnvironmentToRemove(item);
    setIsModalConfirmRemoveOpen(true);
  };

  return (
    <Box
      data-testid="application-item"
      boxStyle="lightGrey"
      spacing="spacing02"
    >
      <GlobalSpinner loading={isLoadingRemoveEnvFromOrgUnit} />
      <Row>
        <Col sm={5} md={4} lg={7}>
          <FlexContainer>
            <ExpandIcon
              data-testid="application-expand"
              name="chevronDown"
              title="chevronDown"
              isExpanded={isExpanded}
              css={{
                marginRight: theme.spacing.spacing00,
              }}
              onClick={toggleIsExpanded}
            />
            <ApplicationItemDescription
              isBusinessContextPage
              application={application}
            />
          </FlexContainer>
        </Col>
        <Col sm={3} md={4} lg={5}>
          <FlexContainer justifyContent="flex-end">
            <ApplicationItemCell title="Monthly Costs">
              <Text weight="medium" tag="div" pr={theme.spacing.spacing02}>
                {application.cost.appMonthlyCosts}
              </Text>
            </ApplicationItemCell>
            <Hidden xs sm>
              <ApplicationItemCell borderLeft title="Monthly Forecast">
                <When condition={appMonthlySavingSuggestions > 0}>
                  <WasteIndicator
                    size="sm"
                    reason="Application has Savings Suggestions"
                    amount={{
                      amount: `${appMonthlySavingSuggestions}`,
                      uri: `${ROUTES.estateRecords.index}?application=${application.id}&page=0&waste=true`,
                    }}
                  />
                </When>
                <Text
                  tag="div"
                  pr={theme.spacing.spacing02}
                  ml={theme.spacing.spacing02}
                  weight="medium"
                >
                  {application.cost.appMonthlyForecast}
                </Text>
              </ApplicationItemCell>
              <ApplicationItemCell borderLeft title="Monthly Budget">
                <Text
                  tag="div"
                  pr={theme.spacing.spacing02}
                  ml={theme.spacing.spacing02}
                  weight="medium"
                >
                  {application.budget}
                </Text>
              </ApplicationItemCell>
            </Hidden>
          </FlexContainer>
        </Col>
      </Row>
      <When condition={isExpanded}>
        <EnvironmentDetails
          showDetach
          applicationId={application.id}
          environments={application.envs}
          onRemoveEnvironment={handleRemoveEnvironment}
        />
      </When>
      <ModalConfirm
        isOpen={isModalConfirmRemoveOpen}
        confirm={() => onRemoveEnvFromOrgUnitHandler(environmentToRemove.nid)}
        actionLabel="Remove"
        contentLabel="Remove environment from organization unit"
        onClose={() => setIsModalConfirmRemoveOpen(false)}
      >
        {generateActionConfirmText(environmentToRemove.name)("delete")(
          "from organization unit"
        )}
      </ModalConfirm>
    </Box>
  );
}
